/**
 * B502 — tenant staff management doors (UNGATED 2026-09-21, Pathik: "for
 * tenants they dont have a way to manage their employees as well with
 * permission right? we should add that as well"). Spec = Figma 338-2
 * (Back office — Staff & permissions). ADMIN-only, riding the existing
 * human pool (immutable custom:tenantId); the permission matrix itself is
 * the authorizer's role tables — these doors edit WHO holds a role, never
 * what a role may do.
 */
import { z } from "zod";
import { publicOp, readOp, writeOp, zEpochMs, zId } from "./common.js";

export const zStaffRole = z.enum(["ADMIN", "MANAGER", "SELLER", "VALIDATOR"]);

const zStaffMember = z
  .object({
    staffId: zId,
    email: z.string().min(3),
    /** Display name, when the invite carried one. */
    name: z.string().min(1).optional(),
    role: zStaffRole,
    enabled: z.boolean(),
    /** Has the invite been accepted (password set)? */
    confirmed: z.boolean(),
    /** "Where they work" — tenant words, tenant configuration. */
    locations: z.array(z.string().min(1)).optional(),
  })
  .strict();

const EXAMPLE_MEMBER = {
  staffId: "b1c2d3e4-staff",
  email: "amy@harborferries.example",
  role: "SELLER",
  enabled: true,
  confirmed: true,
  locations: ["Harbor kiosk"],
};

export const staffList = readOp({
  name: "staff.list",
  permission: "staff:read",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ staff: z.array(zStaffMember) }).strict(),
  exampleRequest: {},
  exampleResponse: { staff: [EXAMPLE_MEMBER] },
});

/** B1055 — what the staff list keeps of a person's name (the admin judges it at the invite's field). */
export const STAFF_NAME_MAX = 120;

export const staffInvite = writeOp({
  name: "staff.invite",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    email: z.string().min(3).max(320),
    role: zStaffRole,
    name: z.string().min(1).max(STAFF_NAME_MAX).optional(),
    locations: z.array(z.string().min(1).max(120)).max(20).optional(),
  },
  response: z
    .object({
      member: zStaffMember,
      /** Shown ONCE (SES is a go-live item) — never stored, never re-readable. */
      temporaryPassword: z.string().min(1),
    })
    .strict(),
  exampleRequest: { email: "amy@harborferries.example", role: "SELLER" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, confirmed: false }, temporaryPassword: "shown-once" },
});

export const staffUpdate = writeOp({
  name: "staff.update",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    staffId: zId,
    role: zStaffRole.optional(),
    locations: z.array(z.string().min(1).max(120)).max(20).optional(),
  },
  response: z.object({ member: zStaffMember }).strict(),
  exampleRequest: { staffId: "b1c2d3e4-staff", role: "MANAGER" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, role: "MANAGER" } },
});

export const staffDisable = writeOp({
  name: "staff.disable",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: { staffId: zId },
  response: z.object({ member: zStaffMember }).strict(),
  exampleRequest: { staffId: "b1c2d3e4-staff" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, enabled: false } },
});

export const staffEnable = writeOp({
  name: "staff.enable",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: { staffId: zId },
  response: z.object({ member: zStaffMember }).strict(),
  exampleRequest: { staffId: "b1c2d3e4-staff" },
  exampleResponse: { member: EXAMPLE_MEMBER },
});

export const staffRemove = writeOp({
  name: "staff.remove",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: { staffId: zId },
  response: z.object({ removed: z.literal(true) }).strict(),
  exampleRequest: { staffId: "b1c2d3e4-staff" },
  exampleResponse: { removed: true },
});

/**
 * B1029 — OPEN THE TILL (RULED by the design seat 2026-10-30 19:4xZ under
 * Pathik's standing instruction; Pathik may overrule). The back office mints
 * a ONE-TIME code (32 random bytes, 60 s, one redemption) for the person
 * signed in, in the role they hold; the POS exchanges it for its own session
 * of the same person and role. Roles that sell only — GATE STAFF never sees
 * the door. The till identity stays the device's (B383).
 */
const zHandoffCode = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const zHandoffRole = z.enum(["ADMIN", "MANAGER", "SELLER"]);

export const staffHandoffMint = writeOp({
  name: "staff.handoff.mint",
  permission: "order:write",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER", "STAFF:SELLER"],
  fields: {},
  response: z
    .object({
      /** Shown ONCE, inside the POS address the door opens; stored only as a digest. */
      code: zHandoffCode,
      expiresAt: zEpochMs,
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: { code: "Q2xhdWRlIG9wZW5zIHRoZSB0aWxsIGZvciBBbXkgbm9", expiresAt: 1_780_000_060_000 },
});

export const staffHandoffRedeem = publicOp({
  name: "staff.handoff.redeem",
  permission: "public",
  fields: { code: zHandoffCode },
  response: z
    .object({
      /** `hnd1.` — the POS session's bearer, a STAFF session of the same person and role. */
      token: z.string().min(1),
      staffId: zId,
      role: zHandoffRole,
      expiresAt: zEpochMs,
    })
    .strict(),
  exampleRequest: { code: "Q2xhdWRlIG9wZW5zIHRoZSB0aWxsIGZvciBBbXkgbm9" },
  exampleResponse: { token: "hnd1.example", staffId: "b1c2d3e4-staff", role: "SELLER", expiresAt: 1_780_003_600_000 },
});
